import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Microscope, Scale, Calculator, Layers, Target, Users, Check, Flame, Youtube, Zap } from "lucide-react";
import { Stars, PromoCountdown } from "../components/Bits";

const YOUTUBE_URL = "https://www.youtube.com/channel/UCS-sv1_P_YZeEfPqTkmGsng";

const FALLBACK_PREVIEW = [
  { category: "lock", stars: 5, player_name: "Aaron Judge", bet: "To Hit a HR", odds: 185 },
  { category: "value", stars: 4, player_name: "Kyle Schwarber", bet: "To Hit a HR", odds: 320 },
  { category: "longshot", stars: 3, player_name: "Gunnar Henderson", bet: "To Hit a HR", odds: 520 },
];

const FEATURES = [
  { icon: Microscope, title: "Research Center", desc: "HR probability, Barrel %, pitcher matchups and hot-streak form for 600+ MLB bats — plus full NFL and college football matchup labs." },
  { icon: Scale, title: "Game Lines & Best Price", desc: "Moneylines, spreads and totals across the major books with the best number flagged. Every sport, one screen." },
  { icon: Zap, title: "Predictors & Live Alerts", desc: "Likely-to-HR board, anytime TD predictor and live scoring alerts the moment they land. Every pick auto-graded in public." },
  { icon: Target, title: "Likely To Cash", desc: "The slate's most likely winners per market, ranked by devigged probability — with a daily model record that resets every morning." },
  { icon: Layers, title: "Parlay & Sim Tools", desc: "Stack legs with combined odds and EV, then play the game before it happens with full game and touchdown simulators." },
  { icon: Calculator, title: "Hit Tracker", desc: "Full transparency. Daily record, ROI, units won and month to date. Every pick gets graded, win or lose." },
];

const TIERS = [
  { key: "free", name: "Free", price: "$0", per: "/mo", tag: "Get a taste", features: ["1 daily pick", "Basic player research", "Hit Tracker access", "EV Calculator"], cta: "Start Free", highlight: false },
  { key: "daypass", name: "Day Pass", price: "$9.99", per: "one time", tag: "Big slate tonight?", features: ["24 hours of full Pro access", "Full board, all picks unlocked", "Live HR Tracker & Weak Spots", "No subscription, no strings"], cta: "Grab a Pass", highlight: false },
  { key: "pro", name: "Pooh Sheet Pro", price: "$29", per: "/mo", tag: "Most popular", features: ["Full daily board: Locks, Value, Long Shots", "Complete Research Center", "Odds Tracker with best line alerts", "Pitcher Analysis, BvP & HRR Targets", "Parlay Builder & EV tools"], cta: "Go Pro", highlight: true },
  { key: "vip", name: "VIP", price: "$79", per: "/mo", tag: "For the sharps", features: ["Everything in Pro", "Live updates", "Premium alerts", "VIP only community channel", "Priority support"], cta: "Go VIP", highlight: false },
];

export default function Landing() {
  const { user } = useAuth();
  const dest = user && user !== false ? "/dashboard" : "/signup";
  const { data: stats } = useQuery({
    queryKey: ["publicStats"],
    queryFn: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`).then((r) => r.data),
  });
  const { data: promo } = useQuery({
    queryKey: ["promo"],
    queryFn: () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/promo`).then((r) => r.data),
  });
  const preview = stats?.preview?.length ? stats.preview : FALLBACK_PREVIEW;
  const hasRecord = (stats?.graded || 0) > 0;
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="font-chalk text-3xl font-bold text-[#a855f7]">The Pooh Sheet</div>
          <div className="flex items-center gap-3">
            <a href={YOUTUBE_URL} target="_blank" rel="noreferrer" data-testid="landing-youtube-btn"
              className="hidden sm:flex items-center gap-1.5 text-sm text-neutral-300 hover:text-red-400 font-medium px-3 py-2 transition-colors duration-200">
              <Youtube size={17} /> YouTube
            </a>
            {user && user !== false ? (
              <Link to="/dashboard" data-testid="landing-dashboard-btn" className="bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase text-sm px-5 py-2 rounded transition-colors duration-200">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login-btn" className="text-sm text-neutral-300 hover:text-white font-medium px-4 py-2 transition-colors duration-200">Log in</Link>
                <Link to="/signup" data-testid="landing-signup-btn" className="bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase text-sm px-5 py-2 rounded transition-colors duration-200">Sign up</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/f5264b37-fb76-4ad8-969a-59110049862c/images/1bd6c3e31b1bdba9d1654658e81a156169d42e100af715ae6a2693adb809dd2f.jpeg)" }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 border border-[#a855f7]/40 bg-[#a855f7]/10 text-[#a855f7] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
              <Flame size={13} /> Every Sport. One Sheet.
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
              Smart Hits.<br />Smart Plays.<br /><span className="text-[#a855f7]">Pooh Sheet Wins.</span>
            </h1>
            <div className="font-chalk text-3xl md:text-4xl text-white mt-5 -rotate-1" data-testid="hero-tagline">
              It's Not A Sin, If You <span className="text-[#22c55e]">WIN!</span>
            </div>
            <p className="mt-5 text-neutral-300 text-base md:text-lg max-w-lg">
              Real numbers across MLB, NFL and college football. Real matchup work, live lines, predictors and a board that gets graded every single night in front of everybody. We do the homework so you move with a plan, not a hunch.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={dest} data-testid="hero-cta" className="bg-[#a855f7] hover:bg-[#9333ea] text-black font-bold uppercase tracking-wider px-8 py-3.5 rounded transition-colors duration-200">
                Get Today's Picks
              </Link>
              <a href="#pricing" data-testid="hero-pricing-link" className="border border-white/20 hover:border-[#a855f7] text-white font-bold uppercase tracking-wider px-8 py-3.5 rounded transition-colors duration-200">
                See Pricing
              </a>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="hero-sports-strip">
              {["MLB", "NFL", "NCAAF", "NBA", "NHL"].map((s) => (
                <span key={s} className="text-[10px] uppercase tracking-widest font-black bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/40 px-2.5 py-1 rounded-full">{s} — Live</span>
              ))}
              {["NCAAB"].map((s) => (
                <span key={s} className="text-[10px] uppercase tracking-widest font-bold text-neutral-400 border border-white/15 px-2.5 py-1 rounded-full">{s} <span className="text-[#a855f7]">Soon</span></span>
              ))}
            </div>
            <div className="mt-10 flex gap-8 font-data text-sm" data-testid="hero-stats-strip">
              {hasRecord ? (
                <>
                  <div><div className={`text-2xl font-bold ${stats.units >= 0 ? "text-[#22c55e]" : "text-red-400"}`}>{stats.units > 0 ? "+" : ""}{stats.units}u</div><div className="text-neutral-500 text-xs uppercase">Units this season</div></div>
                  <div><div className="text-white text-2xl font-bold">{stats.win_pct}%</div><div className="text-neutral-500 text-xs uppercase">Win rate ({stats.wins}-{stats.losses})</div></div>
                  {stats.engine?.graded > 0 ? (
                    <div data-testid="engine-record-public"><div className="gold-text text-2xl font-bold">{stats.engine.wins}-{stats.engine.losses}</div><div className="text-neutral-500 text-xs uppercase">Pick engine record ({stats.engine.win_pct}%)</div></div>
                  ) : (
                    <div><div className="text-[#a855f7] text-2xl font-bold">5+</div><div className="text-neutral-500 text-xs uppercase">Books tracked</div></div>
                  )}
                </>
              ) : (
                <>
                  <div><div className="text-[#22c55e] text-2xl font-bold">LIVE</div><div className="text-neutral-500 text-xs uppercase">Every pick tracked</div></div>
                  <div><div className="text-white text-2xl font-bold">5</div><div className="text-neutral-500 text-xs uppercase">Sports live right now</div></div>
                  <div><div className="text-[#a855f7] text-2xl font-bold">8+</div><div className="text-neutral-500 text-xs uppercase">Books compared</div></div>
                </>
              )}
            </div>
          </div>

          <div className="fade-up-2">
            <div className="chalk-texture border-2 border-white/20 rounded-xl p-6 md:p-8 shadow-2xl rotate-1">
              <div className="font-chalk text-4xl md:text-5xl text-white text-center -rotate-2 mb-6">Today's Board</div>
              {preview.map((p, i) => (
                <div key={i} className="flex items-center justify-between border-b border-white/15 py-3 last:border-0" data-testid={`landing-preview-${p.category}`}>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#a855f7] font-bold">{p.category === "longshot" ? "LONG SHOT" : p.category.toUpperCase()} <Stars n={p.stars} /></div>
                    <div className="font-chalk text-2xl text-white leading-tight">{p.player_name}</div>
                    <div className="text-xs text-neutral-400">{p.bet}</div>
                  </div>
                  <div className="font-data text-lg font-bold text-[#22c55e]">{p.odds > 0 ? `+${p.odds}` : p.odds}</div>
                </div>
              ))}
              <div className="mt-5 text-center">
                <Link to={dest} data-testid="board-preview-cta" className="inline-block font-chalk text-2xl text-[#a855f7] underline decoration-wavy underline-offset-4 hover:text-white transition-colors duration-200">
                  Unlock the full board →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight">Everything a sharp bettor needs</h2>
        <p className="text-neutral-400 mt-2 max-w-xl">Research grade tooling built on live MLB, NFL and college football data. No vibes, just numbers.</p>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="card-surface hover-lift rounded-lg p-6">
              <Icon className="text-[#a855f7] mb-4" size={24} />
              <h3 className="font-display text-xl font-semibold uppercase">{title}</h3>
              <p className="text-neutral-400 text-sm mt-2">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 flex flex-col md:flex-row items-center gap-8">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center shrink-0">
            <Youtube size={28} className="text-red-400" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">Watch the picks break down on YouTube</h2>
            <p className="text-neutral-400 mt-1">Daily slates, HR calls and the reasoning behind every play. Come see how the sheet gets built.</p>
          </div>
          <a href={YOUTUBE_URL} target="_blank" rel="noreferrer" data-testid="youtube-section-cta"
            className="bg-red-500 hover:bg-red-600 text-white font-bold uppercase tracking-wider text-sm px-7 py-3 rounded transition-colors duration-200 shrink-0">
            Subscribe on YouTube
          </a>
        </div>
      </section>

      <section id="pricing" className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight text-center">Pick your edge</h2>
        <p className="text-center text-neutral-400 mt-2">Ride monthly or grab a Day Pass for the big slate. Your call.</p>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {TIERS.map((t) => {
            const promoDay = t.key === "daypass" && promo?.active;
            return (
            <div key={t.key} data-testid={`pricing-${t.key}`}
              className={`rounded-xl p-7 border flex flex-col relative ${t.highlight ? "border-[#a855f7] bg-[#a855f7]/5 gold-glow lg:scale-[1.03]" : promoDay ? "border-[#22c55e]/60 card-surface" : "border-white/10 card-surface"}`}>
              {promoDay && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#22c55e] text-black text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full whitespace-nowrap" data-testid="pricing-promo-badge">
                  $1 Launch Promo · {promo?.ends_at ? <PromoCountdown endsAt={promo.ends_at} /> : "Limited Time"}
                </div>
              )}
              <div className="text-[11px] uppercase tracking-widest text-[#a855f7] font-bold">{t.tag}</div>
              <h3 className="font-display text-2xl font-bold uppercase mt-1">{t.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                {promoDay ? (
                  <>
                    <span className="font-data text-xl text-neutral-500 line-through">$9.99</span>
                    <span className="font-data text-4xl font-bold text-[#22c55e]">$1</span>
                    <span className="text-neutral-500 text-sm">one time</span>
                  </>
                ) : (
                  <>
                    <span className="font-data text-4xl font-bold">{t.price}</span>
                    <span className="text-neutral-500 text-sm">{t.per}</span>
                  </>
                )}
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-neutral-300"><Check size={16} className="text-[#22c55e] shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Link to={user && user !== false ? "/settings" : "/signup"} data-testid={`pricing-cta-${t.key}`}
                className={`mt-6 text-center font-bold uppercase tracking-wider text-sm px-6 py-3 rounded transition-colors duration-200 ${t.highlight ? "bg-[#a855f7] hover:bg-[#9333ea] text-black" : "border border-white/20 hover:border-[#a855f7] text-white"}`}>
                {t.cta}
              </Link>
            </div>
            );
          })}
        </div>
        <p className="text-center text-neutral-500 text-xs mt-6">Secure checkout powered by Stripe. Cancel your membership anytime.</p>
      </section>

      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-neutral-500">
          <div>
            <div className="font-display text-lg font-black uppercase text-white">The <span className="text-[#a855f7]">Pooh Sheet</span></div>
            <div className="font-chalk text-lg text-neutral-400 mt-0.5">It's Not A Sin, If You WIN!</div>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#a855f7] transition-colors duration-200">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#a855f7] transition-colors duration-200">Terms of Service</Link>
            <a href={YOUTUBE_URL} target="_blank" rel="noreferrer" data-testid="footer-youtube-link" className="hover:text-red-400 transition-colors duration-200 flex items-center gap-1"><Youtube size={15} /> YouTube</a>
          </div>
          <div>For entertainment purposes only. Please bet responsibly. 21+.</div>
        </div>
      </footer>
    </div>
  );
}
